// Generic imports
import { useState } from 'react';
import _ from 'lodash';

// MUI imports
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

export const FOLLOW_UP_METHODS = [
    { value: 'call', label: 'Phone call', text: 'by phone call' },
    { value: 'text', label: 'Text / WhatsApp', text: 'by text / WhatsApp' },
    { value: 'email', label: 'Email outside Monada', text: 'by email outside Monada' },
    { value: 'in-person', label: 'In person', text: 'in person' },
    { value: 'other', label: 'Other', text: '' },
];

// A follow-up done outside Monada (a call, a WhatsApp, an email from Outlook) is recorded as a
// comment on the quote. The comment carries a `followUp` marker so the follow-up queue knows the
// quote was followed up, and posting a comment is something any teammate may do on a quote.
export function isFollowUpDone(opportunity) {
    return (opportunity?.updates || []).some(u => u && !u.deleted && u.followUp);
}

function FollowUpDoneDialog({ opportunity, quoteLabel, accountName, me, onSave, onClose }) {
    const [ method, setMethod ] = useState('call');
    const [ note, setNote ] = useState('');
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState(false);

    const methodText = FOLLOW_UP_METHODS.find(m => m.value === method)?.text;
    const canSave = !saving && (method !== 'other' || note.trim().length > 0);

    async function onSubmit() {
        setSaving(true);
        setError(false);
        const trimmed = note.trim();
        const html = `<p><strong>Follow-up done${methodText ? ` ${methodText}` : ''}</strong>${trimmed ? `: ${_.escape(trimmed)}` : ''}</p>`;
        const update = {
            created: new Date().getTime(),
            createdBy: me?.email,
            html,
            files: [],
            followUp: { method, note: trimmed },
        };
        const ok = await onSave({ opportunity, update });
        setSaving(false);
        if (!ok) setError(true);
    }

    return (
        <Dialog open={true} onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>Mark follow-up as done</DialogTitle>
            <DialogContent>
                <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                    Quote {quoteLabel}{accountName ? ` for ${accountName}` : ''}. This is added to the quote's comments and removes it from the follow-up queue.
                </Typography>
                <Typography variant='caption' sx={{ display: 'block', fontWeight: 600, mb: 0.75 }}>How did you follow up?</Typography>
                <ToggleButtonGroup
                    value={method}
                    exclusive
                    size='small'
                    onChange={(e, v) => v && setMethod(v)}
                    sx={{ flexWrap: 'wrap', mb: 2, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '12px', py: 0.5 } }}
                >
                    {FOLLOW_UP_METHODS.map(m => <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size='small'
                    autoFocus
                    label={method === 'other' ? 'How, and what came of it' : 'Comment (optional)'}
                    placeholder='e.g. Spoke with the buyer, decision expected next week'
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />
                {error && <Alert severity='error' sx={{ mt: 2 }}>Couldn't save the follow-up. Please try again.</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant='contained' onClick={onSubmit} disabled={!canSave}>
                    {saving ? 'Saving…' : 'Mark as done'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FollowUpDoneDialog;
